import React, { useState } from "react";
import { SudoButton } from "../sudokit/components/SudoButton";
import ShortcutBadge from "../components/ShortcutBadge";
import CopyToClipboardButton from "./CopyToClipboardButton";
import { EditPopover } from "./EditPopover";
import { cn } from "src/lib/cn";
import IconButton from "src/components/IconButton";
import { WithReset } from "src/sudokit/Layout/BoxReset";

export interface UserFieldConfig {
  key: string;
  label: string;
  value: string | number | boolean | null | undefined;
  defaultValue?: string;
  type?: "text" | "link" | "boolean" | "date" | "custom";
  linkUrl?: string;
  linkNewTab?: boolean;
  shortcut?: {
    key: string;
    label: string;
    onTrigger: () => void;
  };
  onClick?: () => void;
  editable?: boolean;
  onEdit?: () => void;
  copyable?: boolean;
  showCopyIcon?: boolean;
  customRender?: (value: any) => React.ReactNode;
  additionalContent?: React.ReactNode;
  category: string;
  alwaysShow?: boolean;
  className?: string;
  valueClassName?: string;
  bigField?: boolean;
}

export interface UserFieldGridProps {
  fields: UserFieldConfig[];
  categoryOrder?: string[];
}

export default function UserFieldGrid({ fields, categoryOrder }: UserFieldGridProps) {
  const fieldsByCategory = fields.reduce(
    (acc, field) => {
      if (!acc[field.category]) {
        acc[field.category] = [];
      }
      acc[field.category].push(field);
      return acc;
    },
    {} as Record<string, UserFieldConfig[]>,
  );

  const categories = categoryOrder || Object.keys(fieldsByCategory);

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4 mb-6">
      {categories.map((category) => {
        const categoryFields = fieldsByCategory[category] || [];
        if (categoryFields.length === 0) return null;

        return (
          <CategorySection
            key={category}
            category={category}
            fields={categoryFields}
          />
        );
      })}
    </div>
  );
}

interface CategorySectionProps {
  category: string;
  fields: UserFieldConfig[];
}

function CategorySection({ category, fields }: CategorySectionProps) {
  const [showUnused, setShowUnused] = useState(false);

  const usedFields: UserFieldConfig[] = [];
  const unusedFields: UserFieldConfig[] = [];

  fields.forEach((field) => {
    const isEmpty = isFieldEmpty(field.value);
    if (field.alwaysShow || !isEmpty) {
      usedFields.push(field);
    } else {
      unusedFields.push(field);
    }
  });

  return (
    <>
      {category && (
        <div className="col-span-full pl-2.5">
          <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-200">{category}</h3>
        </div>
      )}

      {usedFields.map((field) => (
        <FieldCard
          key={field.key}
          field={field}
        />
      ))}

      {unusedFields.length > 0 && (
        <WithReset className="col-span-full">
          <button
            onClick={() => setShowUnused(!showUnused)}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 flex items-center gap-1 py-2"
          >
            <span>{showUnused ? "▼" : "▶"}</span>
            <span>
              {showUnused ? "Hide" : "Show"} Unused Fields ({unusedFields.length})
            </span>
          </button>

          {showUnused && (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4 mt-2">
              {unusedFields.map((field) => (
                <FieldCard
                  key={field.key}
                  field={field}
                />
              ))}
            </div>
          )}
        </WithReset>
      )}

      <div className="h-2 col-span-2" />
    </>
  );
}

interface FieldCardProps {
  field: UserFieldConfig;
}

function FieldCard({ field }: FieldCardProps) {
  const displayValue = getDisplayValue(field);
  const isEmpty = isFieldEmpty(field.value);

  const isEditPopoverOpen = field.editable && !!field.additionalContent;

  return (
    <div className="bg-gray-50 hover:bg-gray-100/80 transition-colors px-3 py-2 rounded-lg flex flex-col justify-start gap-1">
      <dt className={cn("text-sm font-medium text-gray-500", field.bigField ? "h-auto" : "h-7")}>
        <div className="flex items-center justify-between w-full gap-4">
          <span className="leading-4">{field.label}</span>

          <div className="flex items-center justify-end gap-1">
            {field.editable && field.onEdit && (
              <EditPopover
                open={!!isEditPopoverOpen}
                onOpenChange={() => {
                  field.onEdit!();
                }}
                trigger={
                  <div>
                    <IconButton
                      variant="secondary"
                      src="/assets/pencil.svg"
                      className="h-5 w-5"
                      iconSize={22}
                    />
                  </div>
                }
              >
                {field.additionalContent}
              </EditPopover>
            )}

            {field.copyable && field.showCopyIcon !== false && !isEmpty && (
              <CopyToClipboardButton
                textToCopy={String(field.value)}
                displayText=""
                iconClassName="w-4 h-4"
                useIcon
              />
            )}

            {field.shortcut && (
              <div className="h-5">
                <ShortcutBadge
                  keyValue={field.shortcut.key}
                  label={field.shortcut.label}
                  onTrigger={field.shortcut.onTrigger}
                />
              </div>
            )}
          </div>
        </div>
      </dt>
      <dd className={`text-sm text-gray-900 ${field.valueClassName || ""}`}>
        {field.customRender ? (
          field.customRender(field.value)
        ) : (
          <>
            <div className={`flex gap-1 items-start ${field.className || ""}`}>
              {renderFieldValue(field, displayValue, isEmpty)}

              {field.onClick && !isEmpty && (
                <SudoButton
                  onClick={(e) => {
                    e.preventDefault();
                    field.onClick!();
                  }}
                  variant="secondary"
                  size="xsmall"
                >
                  View Details
                </SudoButton>
              )}
            </div>
          </>
        )}
      </dd>
    </div>
  );
}

function renderFieldValue(field: UserFieldConfig, displayValue: string, isEmpty: boolean): React.ReactNode {
  if (isEmpty) {
    return <span className="text-gray-600">{displayValue}</span>;
  }

  if (field.type === "link" && field.linkUrl) {
    return (
      <>
        <a
          href={field.linkUrl}
          target={field.linkNewTab ? "_blank" : undefined}
          rel={field.linkNewTab ? "noopener noreferrer" : undefined}
          className="text-app-primary-cta hover:text-app-primary-cta-dark hover:underline font-mono"
        >
          {displayValue}
        </a>
      </>
    );
  }

  if (field.copyable && field.type !== "link") {
    return (
      <CopyToClipboardButton
        truncate
        textToCopy={String(field.value)}
        displayText={displayValue}
        className="text-mono justify-start items-start"
      />
    );
  }

  return displayValue;
}

function getDisplayValue(field: UserFieldConfig): string {
  if (isFieldEmpty(field.value)) {
    return field.defaultValue || "N/A";
  }

  if (field.type === "boolean") {
    return field.value ? "True" : "False";
  }

  return String(field.value);
}

function isFieldEmpty(value: any): boolean {
  return value === null || value === undefined || value === "" || value === false;
}
